import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, Wallet, Building2, Banknote, ExternalLink } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

// FIN-3 — HeroSaldoContasCard adaptativo OF ON / OF OFF.
//
// OF ON: ícone Banknote verde, título "Open Finance", footer com count
// de instituições + conexões.
// OF OFF (default): ícone Wallet cyan, título "Suas Contas", footer com
// count de cartões + contas bancárias, banner sutil de upsell.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function HiddenAmount() {
  return <span className="font-mono">R$ ••••••</span>;
}

function plural(n, sing, plur) {
  return `${n} ${n === 1 ? sing : plur}`;
}

export default function HeroSaldoContasCard({
  totalBalance,        // { total_balance, accounts_count, by_type, ... }
  creditCards,
  bankAccounts,
  isOpenFinance,
  distinctInstitutions,
  loading,
}) {
  const { user } = useAuth();
  const userId = user?.id;

  // Toggle eye persistido (mesma key da Visão pra UX consistente)
  const STORAGE_KEY = userId ? `nexo_visao_balance_hidden_${userId}` : null;
  const [hidden, setHidden] = useState(() => {
    if (!STORAGE_KEY) return false;
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    if (!STORAGE_KEY) return;
    try { localStorage.setItem(STORAGE_KEY, hidden ? "1" : "0"); } catch (_) { /* noop */ }
  }, [STORAGE_KEY, hidden]);

  const total = Number(totalBalance?.total_balance ?? 0);
  const cardsCount = creditCards?.length ?? 0;
  const banksCount = bankAccounts?.length ?? 0;
  const Icon = isOpenFinance ? Banknote : Wallet;
  const title = isOpenFinance ? "Open Finance" : "Suas Contas";
  const iconBg = isOpenFinance ? "bg-brand-green/15" : "bg-brand-cyan/15";
  const iconBorder = isOpenFinance ? "border-brand-green/30" : "border-brand-cyan/30";
  const iconColor = isOpenFinance ? "text-brand-green" : "text-brand-cyan";

  return (
    <Card
      variant="elevated"
      className="glass-card-strong gradient-border-cyan glow-hover p-6 md:p-8 relative overflow-hidden"
      data-testid="contas-hero"
    >
      {/* Top row */}
      <div className="flex items-start justify-between mb-5 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={cn(
            "w-10 h-10 rounded-xl border flex items-center justify-center shrink-0",
            iconBg, iconBorder,
          )}>
            <Icon className={cn("w-5 h-5", iconColor)} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-foreground text-h2 leading-tight" data-testid="contas-hero-title">
              {title}
            </h2>
            <p className="text-caption text-foreground-muted">Saldo em contas</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setHidden(h => !h)}
          className="p-2 rounded-lg text-foreground-muted hover:bg-surface-card-hover hover:text-foreground transition-colors"
          aria-label={hidden ? "Mostrar saldo" : "Esconder saldo"}
          data-testid="contas-hero-toggle"
        >
          {hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
      </div>

      {/* Heroic balance */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
        className="mb-4"
        data-testid="contas-hero-amount"
      >
        <div
          className={cn(
            "font-display font-bold leading-[0.95] tracking-[-0.02em] tabular-nums",
            "text-[44px] md:text-[64px] lg:text-[80px]",
            "text-brand-green",
          )}
        >
          {loading ? "…" : (hidden ? <HiddenAmount /> : fmtBRL(total))}
        </div>
      </motion.div>

      {/* Footer */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <p className="text-caption text-foreground-muted" data-testid="contas-hero-footer">
          {isOpenFinance ? (
            <>
              {plural(distinctInstitutions, "instituição", "instituições")}
              {" · "}
              {plural(cardsCount, "cartão", "cartões")}
              {" · "}
              {plural(banksCount, "conta bancária", "contas bancárias")}
            </>
          ) : (
            <>
              {plural(cardsCount, "cartão", "cartões")}
              {" · "}
              {plural(banksCount, "conta bancária", "contas bancárias")}
            </>
          )}
        </p>

        {!isOpenFinance && (
          <Badge
            variant="info"
            className="self-start md:self-auto inline-flex items-center gap-1.5 px-2.5 py-1 text-caption font-normal"
            data-testid="contas-hero-of-banner"
          >
            <Building2 className="w-3.5 h-3.5" aria-hidden="true" />
            Conecte Open Finance pra sincronizar automaticamente
            <ExternalLink className="w-3 h-3 opacity-70" aria-hidden="true" />
          </Badge>
        )}
      </div>
    </Card>
  );
}
